import { usePageCRUDActions } from '@mm/state/actions/PageCRUDActions';
import { selectPagesDictionary } from '@mm/state/selectors/pageSelectors';
import { useSafeTimeout } from '@mm/hooks';
import React, { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

// Note: the goal is to lift state up to prevent each Outgoing message from
// firing independent calls to BE for pages, which results in multiple duplicte
// requests to the same page currently

export const useCurrentPageName = () => {
  const reduxPages = useSelector(selectPagesDictionary);

  const { safeTimeout } = useSafeTimeout();
  const pageCRUDActions = usePageCRUDActions();
  const currentPageNames = useRef<
    Record<
      number,
      { status: 'queued' | 'loading' | 'retrieved'; name: string | JSX.Element }
    >
  >({});

  // Note: work around since otherwise results in circular references due to JSX elements
  const currentPageNamesToCompare = JSON.stringify(
    Object.entries(currentPageNames.current).map(([key, v]) => ({
      key,
      value: v.status,
    })),
  );

  const getCurrentPageName = useCallback(
    (currentPageId: number | undefined) => {
      if (currentPageId === undefined || currentPageId === null) return '';

      if (currentPageNames.current[currentPageId]) {
        return currentPageNames.current[currentPageId].name;
      }

      if (reduxPages[String(currentPageId)]) {
        currentPageNames.current[currentPageId] = {
          status: 'retrieved',
          name: reduxPages[currentPageId].name || '',
        };
        return currentPageNames.current[currentPageId].name;
      }

      currentPageNames.current[currentPageId] = {
        status: 'queued',
        name: (
          <>
            <em>Loading...</em>
          </>
        ),
      };

      return currentPageNames.current[currentPageId].name;
    },
    [currentPageNamesToCompare, JSON.stringify(reduxPages)],
  );

  useEffect(() => {
    const getPages = async () => {
      const pagesToFetch = Object.entries(currentPageNames.current)
        .filter(([k, v]) => !!v)
        .filter(([k, v]) => v.status === 'queued')
        .map(([k, v]) => k)
        .map(s => Number(s));

      const setPageLoading = (pageId: number) => {
        currentPageNames.current[pageId] = {
          status: 'loading',
          name: (
            <>
              <em>Loading...</em>
            </>
          ),
        };
      };

      const getPages = async (pageIds: number[]) => {
        const pages = await pageCRUDActions.batchRetrieveByIds(pageIds);

        pageIds.forEach(id => {
          const foundPage = pages.find(p => p.id === id);

          if (foundPage) {
            currentPageNames.current[id] = {
              status: 'retrieved',
              name: foundPage?.name || '',
            };
          } else {
            currentPageNames.current[id] = {
              status: 'retrieved',
              name: (
                <>
                  {'#'}
                  {id} <em>(Deleted Dialogue)</em>
                </>
              ),
            };
          }
        });
      };

      if (pagesToFetch.length) {
        pagesToFetch.forEach(p => setPageLoading(p));
        getPages(pagesToFetch);
      }
    };

    safeTimeout(getPages, 200);
  }, [currentPageNamesToCompare]);

  return getCurrentPageName;
};
